import { useEffect, useRef } from 'react';
import { useNavbar } from '@/context/NavbarContext';
import './navbar.scss';

export interface NavbarProps {
  onMenuClick?: () => void;
  showBackButton?: boolean;
  onBackClick?: () => void;
  title?: string;
  className?: string;
}

/**
 * Navbar - Top navigation bar for the app
 *
 * Features:
 * - Hamburger menu button (left)
 * - Optional back button
 * - Optional title/branding
 * - Responsive design
 *
 * @param onMenuClick - Callback when hamburger menu is clicked
 * @param showBackButton - Show back button instead of menu
 * @param onBackClick - Callback when back button is clicked
 * @param title - Optional title to display
 * @param className - Additional CSS classes
 */
export function Navbar({
  onMenuClick,
  showBackButton: propShowBackButton = false,
  onBackClick: propOnBackClick,
  title,
  className = '',
}: NavbarProps) {
  const navRef = useRef<HTMLElement>(null);
  const { title: contextTitle, showBackButton: contextShowBackButton, onBackClick: contextOnBackClick } = useNavbar();

  // Priorità: context > props > defaults
  // Context values override props (set by pages)
  const displayTitle = title || contextTitle;
  const showBackButton = contextShowBackButton || propShowBackButton;
  const onBackClick = contextOnBackClick || propOnBackClick;

  /**
   * Measure navbar height and set CSS custom property dynamically
   * This allows the AppLayout to adjust its height without hardcoded values
   */
  useEffect(() => {
    const updateNavbarHeight = () => {
      if (navRef.current) {
        const height = navRef.current.offsetHeight;
        document.documentElement.style.setProperty('--navbar-height', `${height}px`);
      }
    };

    // Update on mount
    updateNavbarHeight();

    // Update on window resize
    window.addEventListener('resize', updateNavbarHeight);

    return () => {
      window.removeEventListener('resize', updateNavbarHeight);
    };
  }, []);

  return (
    <nav ref={navRef} className={`navbar ${className}`}>
      <div className="navbar__container">
        {/* Menu/Back Button */}
        <button
          className="navbar__menu-button"
          onClick={showBackButton ? onBackClick : onMenuClick}
          aria-label={showBackButton ? 'Back' : 'Menu'}
        >
          {showBackButton ? (
            // Back arrow icon
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          ) : (
            // Hamburger menu icon
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Optional title/branding */}
        {displayTitle && <h1 className="navbar__title">{displayTitle}</h1>}

        {/* Spacer for alignment */}
        <div className="navbar__spacer" />
      </div>
    </nav>
  );
}

export default Navbar;
